/**
 * Session de l'administrateur de plateforme.
 *
 * Cette application n'accepte **que** le role PLATFORM_ADMIN. Ce n'est pas un
 * garde de route parmi d'autres : un compte de restaurant qui se connecte ici
 * est refuse a la porte, et sa session immediatement fermee.
 *
 * Voir docs/adr/0015-back-office-de-plateforme-separe.md.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthUserDTO } from '@scan2eat/shared';

import { apiFetch, AUTH_SCOPE, setAccessToken } from '../api/client.js';
import { labels } from '../labels.js';

/** Erreur affichee quand un compte de restaurant tente d'entrer ici. */
export class NotPlatformAdminError extends Error {
  constructor() {
    super(labels.auth.notPlatformAdmin);
    this.name = 'NotPlatformAdminError';
  }
}

interface AuthContextValue {
  user: AuthUserDTO | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit etre utilise dans AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUserDTO | null>(null);
  const [loading, setLoading] = useState(true);

  const forget = useCallback(async () => {
    setAccessToken(null);
    setUser(null);
    try {
      await apiFetch(`/admin/auth/logout${AUTH_SCOPE}`, { method: 'POST' });
    } catch {
      /* Deja deconnecte : rien de plus a faire. */
    }
  }, []);

  useEffect(() => {
    apiFetch<{ user: AuthUserDTO }>('/admin/auth/me')
      .then((data) => {
        // Une session de restaurant ouverte ailleurs ne donne pas acces ici.
        if (data.user.role === 'PLATFORM_ADMIN') setUser(data.user);
      })
      .catch(() => { /* Pas de session : la page de connexion s'affiche. */ })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ accessToken: string; user: AuthUserDTO }>(`/admin/auth/login${AUTH_SCOPE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (data.user.role !== 'PLATFORM_ADMIN') {
      // Les identifiants sont bons, mais pas pour cette porte : on referme la
      // session ouverte par la connexion.
      setAccessToken(data.accessToken);
      await forget();
      throw new NotPlatformAdminError();
    }

    setAccessToken(data.accessToken);
    setUser(data.user);
  }, [forget]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout: forget }}>
      {children}
    </AuthContext.Provider>
  );
}
